import { Heading, Button as ButtonC, Spacer, useToast, VStack } from '@chakra-ui/react';
import { useLoginMutation } from '@partiu/api/auth/hooks';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { ShowPasswordToggle } from '../../../../../../packages/ui/components/atoms/ShowPasswordToggle';
import { SectionTitle } from '../../../../../../packages/ui/components/atoms/Title';
import { useProfile } from '../../../../../../packages/ui/contexts/ProfileProvider';
import { useTheme } from '../../../contexts/ThemeProvider';
import { Text } from '../../../components/atoms/Text';
import { Input } from '../../../components/atoms/Input';
import { Button } from '../../../components/atoms/Button';
import { validarCNPJ, validarCPF } from '@partiu/ui/validateDocuments';
import { masks } from '@partiu/ui/masks';
import { Footer } from '../../../components/organisms/Footer';

export function LoginForm({ setExClient }: { setExClient?: (value: boolean) => void }) {
  const navigate = useNavigate();
  const toast = useToast({ isClosable: true });
  const { name, idp, fetchBenefits, exClientModule, integratedLogin, id, colors } = useTheme();
  const { updateProfile } = useProfile();
  const [showPassword, setShowPassword] = useState(false);

  const formSchema = z.object({
    username: z.string(),
    password: z.string().min(1),
  });

  const methods = useForm({
    resolver: zodResolver(formSchema),
  });

  const { control, handleSubmit } = methods;

  const loginMutation = useLoginMutation({
    agreementId: id ? id : undefined,
    integratedLogin: integratedLogin ? integratedLogin : undefined,
    onSuccess() {
      updateProfile({});
      navigate('/');
      if (id) {
        fetchBenefits(id);
      }
    },
    onError(error) {
      toast({ title: 'Oops!', description: error, status: 'error' });
    },
  });

  const isEmail = (value: string) => /\S+@\S+\.\S+/.test(value);

  const message = integratedLogin
    ? 'Para acessar o clube, utilize o mesmo login e senha do site'
    : 'Faça login com um CPF, CNPJ ou Email vinculado';

  return (
    <VStack w={'100%'} align={'flex-start'} spacing={4}>
      <SectionTitle display={'flex'} gap={1} flexDir="row" fontSize={14} fontWeight={600}>
        <Text fontSize={14}>
          {message}
          <span
            style={{
              color: colors.primaryColor,
            }}
          >{` ${name}`}</span>
        </Text>
      </SectionTitle>
      <Heading fontWeight={700} letterSpacing={'-0.045em'} fontFamily={'Glory'} fontSize={'36px'}>
        Login
      </Heading>
      <form
        onSubmit={handleSubmit(({ username, password }) => {
          if (integratedLogin) {
            loginMutation.mutate({ username, password, idp });
          } else if (isEmail(username) && id) {
            loginMutation.mutate({ username, password });
          } else if ((validarCPF(username) || validarCNPJ(username)) && id) {
            loginMutation.mutate({ username: masks.unmask(username), password });
          } else {
            toast({ title: 'Erro', description: 'Por favor, insira um email ou CPF/CNPJ válido.', status: 'error' });
          }
        })}
        style={{ width: '100%' }}
      >
        <VStack w={'100%'} spacing={7}>
          <VStack w={'100%'} spacing={4}>
            <Input
              control={control}
              name="username"
              label={integratedLogin ? 'Usuário' : undefined}
              login={integratedLogin ? false : true}
            />
            <Input
              control={control}
              name="password"
              label="Senha"
              type={showPassword ? 'text' : 'password'}
              rightElement={<ShowPasswordToggle value={showPassword} onChange={setShowPassword} />}
            />
          </VStack>
          <ButtonC
            alignSelf={'flex-end'}
            variant={'link'}
            onClick={() => {
              if (integratedLogin) {
                toast({
                  title: 'Oops!',
                  description: 'Por favor, entre em contato com o suporte para recuperar sua senha.',
                  status: 'error',
                });
              } else {
                window.open(`https://api.partiu.com.br/password_reset/?agreement_id=${id}`, '_blank');
              }
            }}
            color={colors.primaryColor}
          >
            <Heading fontWeight={500} fontSize={'16px'}>
              Esqueci minha senha
            </Heading>
          </ButtonC>

          <Button type="submit" isLoading={loginMutation.isLoading}>
            ENTRAR
          </Button>
          {exClientModule && (
            <Button
              backgroundColor="transparent"
              borderColor={colors.primaryColor}
              borderWidth={1}
              color={colors.primaryColor}
              onClick={() => setExClient && setExClient(true)}
              isLoading={loginMutation.isLoading}
            >
              Entrar como Ex-Cliente
            </Button>
          )}
          <Text
            color="#747474"
            margin="auto"
            textAlign={'center'}
            alignItems={'center'}
            gap={1}
            fontSize={14}
            fontWeight={400}
          >
            Cancelou o seu plano?{' '}
            <span onClick={() => navigate('/contato')} style={{ cursor: 'pointer', color: colors.primaryColor }}>
              Clique aqui
            </span>
            , entre em contato conosco.
          </Text>
        </VStack>
      </form>
      <Footer />
      <Spacer
        marginBottom={{
          base: '50px',
          md: '0px',
        }}
      />
    </VStack>
  );
}
